/**
 * 🎨 FOOTER FLOATING TOOLBAR 2025 - Button-Style Design
 * 
 * Floating Toolbar für Footer-Element im Button-Toolbar-Style
 * - Icon Buttons statt Tabs
 * - Pill-shaped Design mit Gradient
 * - Expandable Sections
 */

import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useDebouncedCallback } from '../../hooks/useDebounce';
import { useFont } from '../../Design/FontContext';
import { titleFonts, textFonts } from '../../utils/fontsHelper';
import './FooterFloatingToolbar.css';
import './TipTapInlineEditor.css'; // ✅ Import für tiptap-font-family-option Styling

function FooterFloatingToolbar({
    element,
    onClose,
    onStyleChange,
    colors = {},
    fonts = {},
    anchorRef,
    isMobileLayout = false
}) {
    // 🎯 Font Context für Custom Fonts
    const fontContext = useFont();
    const [showToolbar, setShowToolbar] = useState(false); // ✅ WICHTIG: Interne Toolbar State!
    const [isDragging, setIsDragging] = useState(false);
    const [device, setDevice] = useState(isMobileLayout ? 'mobile' : 'desktop');
    const [toolbarPosition, setToolbarPosition] = useState({ top: 80, left: window.innerWidth / 2 });
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [isManuallyPositioned, setIsManuallyPositioned] = useState(false);
    const toolbarRef = useRef(null);
    
    // ✅ Expanded Section (nur eine Section kann auf einmal offen sein)
    const [expandedSection, setExpandedSection] = useState(null);
    
    // ⚡ OPTIMISTIC UI: Lokaler State für Text-Eingaben (SOFORTIGES Feedback)
    const [localTextValues, setLocalTextValues] = useState({
        sloganText: element.sloganText || ''
    });
    
    // ⚡ Debounced Parent Update (300ms für Text-Eingaben)
    const debouncedTextUpdate = useDebouncedCallback((updates) => {
        console.log('💾 [DEBOUNCED] Saving text changes to Firebase:', updates);
        onStyleChange(updates);
    }, 300);
    
    // ✅ Synchronisiere localTextValues mit element (wenn element von außen geändert wird)
    useEffect(() => {
        setLocalTextValues({
            sloganText: element.sloganText || ''
        });
    }, [element.sloganText]);
    
    // ⚡ Handle Text Change (OPTIMISTIC: Sofort lokal + Debounced zu Parent)
    const handleTextChange = (field, value) => {
        console.log('⚡ [OPTIMISTIC] Text changed locally:', { field, value });
        
        // ✅ Sofortige lokale State-Update (INSTANT Feedback)
        setLocalTextValues(prev => ({ ...prev, [field]: value }));
        
        // ✅ Debounced Parent-Update (nach 300ms)
        debouncedTextUpdate({ [field]: value });
    };
    
    // ✅ Show Toolbar on Mount mit Delay (damit Click Event durch ist)
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setShowToolbar(true);
        }, 50); // 50ms Delay beim ersten Öffnen
        
        return () => clearTimeout(timeoutId);
    }, []);
    
    // ✅ Synchronisiere Device Toggle mit isMobileLayout
    useEffect(() => {
        setDevice(isMobileLayout ? 'mobile' : 'desktop');
    }, [isMobileLayout]);
    
    // 🎯 Drag Handlers
    const handleMouseDown = (e) => {
        const clickedOnControl = e.target.closest('input, select, button, textarea');
        if (clickedOnControl) return;
        
        e.preventDefault();
        e.stopPropagation();
        
        setIsDragging(true);
        setDragOffset({
            x: e.clientX - toolbarPosition.left,
            y: e.clientY - toolbarPosition.top
        });
    };
    
    const handleMouseMove = (e) => {
        if (!isDragging) return;
        
        const newLeft = e.clientX - dragOffset.x;
        const newTop = e.clientY - dragOffset.y;
        
        const maxLeft = window.innerWidth - (toolbarRef.current?.offsetWidth || 500);
        const maxTop = window.innerHeight - (toolbarRef.current?.offsetHeight || 400);
        
        setToolbarPosition({
            left: Math.max(0, Math.min(newLeft, maxLeft)),
            top: Math.max(0, Math.min(newTop, maxTop))
        });
    };
    
    const handleMouseUp = () => {
        setIsDragging(false);
        setIsManuallyPositioned(true);
    };
    
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging]);
    
    // ✅ Close on outside click - EXACTLY like Button Toolbar
    useEffect(() => {
        if (!showToolbar) return; // ✅ CRITICAL: Nur registrieren wenn Toolbar wirklich angezeigt wird!
        
        const handleClickOutside = (event) => {
            // ✅ Check via closest() - funktioniert auch nach Re-Renders!
            const clickedInToolbar = event.target.closest('.footer-floating-toolbar-portal');
            const clickedInAnchor = anchorRef && anchorRef.current && anchorRef.current.contains(event.target);
            
            // Close only if clicked OUTSIDE of toolbar AND anchor
            if (!clickedInToolbar && !clickedInAnchor) {
                setExpandedSection(null);
                onClose();
            }
        };
        
        // ✅ Use mousedown to catch event BEFORE React's onClick
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showToolbar, onClose, anchorRef]); // ✅ showToolbar in dependencies!

    // ✅ Close on Escape
    useEffect(() => {
        if (!showToolbar) return; // ✅ Nur wenn Toolbar angezeigt wird
        
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [showToolbar, onClose]);

    // ✅ Social Media Icon Options
    const socialIconOptions = [
        { value: 'fa-facebook-f', label: 'Facebook' },
        { value: 'fa-instagram', label: 'Instagram' },
        { value: 'fa-twitter', label: 'Twitter' },
        { value: 'fa-linkedin-in', label: 'LinkedIn' },
        { value: 'fa-youtube', label: 'YouTube' },
        { value: 'fa-tiktok', label: 'TikTok' },
        { value: 'fa-pinterest', label: 'Pinterest' },
        { value: 'fa-github', label: 'GitHub' },
        { value: 'fa-whatsapp', label: 'WhatsApp' },
        { value: 'fa-telegram', label: 'Telegram' }
    ];

    // ✅ Handle Logo Upload
    const handleLogoUpload = async (file) => {
        if (!file) return;
        
        if (file.size > 2 * 1024 * 1024) {
            alert('File is too big (max. 2MB).');
            return;
        }
        
        try {
            const { getStorage, ref: storageRef, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
            const { getAuth } = await import('firebase/auth');
            
            const storage = getStorage();
            const auth = getAuth();
            
            if (!auth.currentUser) {
                alert('You must be logged in to upload images.');
                return;
            }
            
            const userId = auth.currentUser.uid;
            const imageRef = storageRef(storage, `pages/${userId}/footer-logos/logo_${Date.now()}_${file.name}`);
            const uploadTask = uploadBytesResumable(imageRef, file);

            uploadTask.on('state_changed', 
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    console.log(`📊 Logo upload progress: ${progress}%`);
                }, 
                (error) => {
                    console.error('❌ Logo upload error:', error);
                    alert('Upload failed. Please try again.');
                }, 
                () => {
                    getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                        onStyleChange({ logoUrl: downloadURL });
                        console.log('✅ Logo uploaded successfully:', downloadURL);
                    });
                }
            );
        } catch (error) {
            console.error('Firebase import error:', error);
            alert('Upload failed. Please try again.');
        }
    };

    const handleLogoUploadClick = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        
        input.onchange = (e) => {
            const file = e.target.files[0];
            if (file) {
                handleLogoUpload(file);
            }
        };
        
        input.click();
    };

    // ✅ Add/Update/Delete Handlers
    const handleAddSocialLink = () => {
        const currentSocials = element.socialLinks || [];
        onStyleChange({ socialLinks: [...currentSocials, { icon: 'fa-link', url: '' }] });
    };

    const handleUpdateSocialLink = (index, field, value) => {
        const currentSocials = [...(element.socialLinks || [])];
        currentSocials[index] = { ...currentSocials[index], [field]: value };
        onStyleChange({ socialLinks: currentSocials });
    };

    const handleDeleteSocialLink = (index) => {
        const currentSocials = [...(element.socialLinks || [])];
        currentSocials.splice(index, 1);
        onStyleChange({ socialLinks: currentSocials });
    };

    const handleAddNavLink = () => {
        const currentLinks = element.navigationLinks || [];
        onStyleChange({ navigationLinks: [...currentLinks, { label: 'New Link', url: '#' }] });
    };

    const handleUpdateNavLink = (index, field, value) => {
        const currentLinks = [...(element.navigationLinks || [])];
        currentLinks[index] = { ...currentLinks[index], [field]: value };
        onStyleChange({ navigationLinks: currentLinks });
    };

    const handleDeleteNavLink = (index) => {
        const currentLinks = [...(element.navigationLinks || [])];
        currentLinks.splice(index, 1);
        onStyleChange({ navigationLinks: currentLinks });
    };

    // ✅ Render Expanded Section Content
    const renderExpandedContent = () => {
        switch (expandedSection) {
            case 'logo':
                return (
                    <div className="footer-section-content">
                        {element.logoUrl ? (
                            <div className="footer-logo-preview">
                                <img src={element.logoUrl} alt="Logo" />
                                <button className="footer-btn-change" onClick={handleLogoUploadClick}>
                                    Change Logo
                                </button>
                            </div>
                        ) : (
                            <button className="footer-btn-upload" onClick={handleLogoUploadClick}>
                                <i className="fas fa-cloud-upload-alt"></i>
                                Upload Logo
                            </button>
                        )}
                        
                        <div className="footer-device-toggle">
                            <button 
                                className={device === 'desktop' ? 'active' : ''}
                                onClick={() => setDevice('desktop')}
                            >
                                Desktop
                            </button>
                            <button 
                                className={device === 'mobile' ? 'active' : ''}
                                onClick={() => setDevice('mobile')}
                            >
                                Mobile
                            </button>
                        </div>
                        
                        <div className="footer-slider-control">
                            <span className="footer-slider-label">Width</span>
                            <input 
                                type="range"
                                className="footer-slider"
                                min="50"
                                max="300"
                                value={device === 'mobile' 
                                    ? (element.logoWidthMobile || element.logoWidth || 120)
                                    : (element.logoWidth || 150)
                                }
                                onChange={(e) => onStyleChange(
                                    device === 'mobile' 
                                        ? { logoWidthMobile: parseInt(e.target.value) }
                                        : { logoWidth: parseInt(e.target.value) }
                                )}
                                onClick={(e) => e.stopPropagation()}
                            />
                            <span className="footer-slider-value">{device === 'mobile' 
                                ? (element.logoWidthMobile || element.logoWidth || 120)
                                : (element.logoWidth || 150)}px</span>
                        </div>
                    </div>
                );

            case 'slogan':
                return (
                    <div className="footer-section-content">
                        <textarea
                            className="footer-textarea"
                            placeholder="Enter your slogan..."
                            value={localTextValues.sloganText}
                            onChange={(e) => handleTextChange('sloganText', e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            rows="2"
                        />
                        
                        <div className="footer-device-toggle">
                            <button 
                                className={device === 'desktop' ? 'active' : ''}
                                onClick={() => setDevice('desktop')}
                            >
                                Desktop
                            </button>
                            <button 
                                className={device === 'mobile' ? 'active' : ''}
                                onClick={() => setDevice('mobile')}
                            >
                                Mobile
                            </button>
                        </div>
                        
                        <div className="footer-slider-control">
                            <span className="footer-slider-label">Size</span>
                            <input 
                                type="range"
                                className="footer-slider"
                                min="10"
                                max="24"
                                value={device === 'mobile' 
                                    ? (element.sloganFontSizeMobile || element.sloganFontSize || 14)
                                    : (element.sloganFontSize || 16)
                                }
                                onChange={(e) => onStyleChange(
                                    device === 'mobile' 
                                        ? { sloganFontSizeMobile: parseInt(e.target.value) }
                                        : { sloganFontSize: parseInt(e.target.value) }
                                )}
                                onClick={(e) => e.stopPropagation()}
                            />
                            <span className="footer-slider-value">{device === 'mobile' 
                                ? (element.sloganFontSizeMobile || element.sloganFontSize || 14)
                                : (element.sloganFontSize || 16)}px</span>
                        </div>
                    </div>
                );

            case 'socials':
                return (
                    <div className="footer-section-content">
                        {/* ✨ Icon Container Toggle */}
                        <div className="footer-toggle-row">
                            <label>Icon Container</label>
                            <label className="footer-switch">
                                <input 
                                    type="checkbox"
                                    checked={element.socialIconContainerEnabled || false}
                                    onChange={(e) => onStyleChange({ socialIconContainerEnabled: e.target.checked })}
                                />
                                <span className="footer-slider"></span>
                            </label>
                        </div>
                        
                        {/* Icon Container Settings */}
                        {element.socialIconContainerEnabled && (
                            <>
                                {/* Container Shape */}
                                <div className="footer-control-group">
                                    <label>Container Shape</label>
                                    <div className="footer-shape-buttons">
                                        <button
                                            className={`footer-shape-btn ${(element.socialIconContainerShape || 'circle') === 'circle' ? 'active' : ''}`}
                                            onClick={() => onStyleChange({ socialIconContainerShape: 'circle' })}
                                            title="Circle"
                                        >
                                            <i className="fas fa-circle"></i>
                                        </button>
                                        <button
                                            className={`footer-shape-btn ${element.socialIconContainerShape === 'rounded' ? 'active' : ''}`}
                                            onClick={() => onStyleChange({ socialIconContainerShape: 'rounded' })}
                                            title="Rounded Square"
                                        >
                                            <i className="fas fa-square"></i>
                                        </button>
                                        <button
                                            className={`footer-shape-btn ${element.socialIconContainerShape === 'square' ? 'active' : ''}`}
                                            onClick={() => onStyleChange({ socialIconContainerShape: 'square' })}
                                            title="Square"
                                        >
                                            <i className="fas fa-stop"></i>
                                        </button>
                                    </div>
                                </div>
                                
                                {/* Container Background - Brand Colors */}
                                <div className="footer-color-group">
                                    <span className="footer-color-label">Container Background</span>
                                    <div className="button-brand-colors button-brand-colors-small">
                                        {/* Transparent Option */}
                                        <button
                                            type="button"
                                            className="button-brand-color button-brand-color-small"
                                            style={{ 
                                                background: 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc)',
                                                backgroundSize: '10px 10px',
                                                backgroundPosition: '0 0, 5px 5px'
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                onStyleChange({ socialIconContainerBg: 'transparent' });
                                            }}
                                            title="Transparent"
                                        />
                                        {Object.values(colors).map((brandColor, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                className="button-brand-color button-brand-color-small"
                                                style={{ backgroundColor: brandColor }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    onStyleChange({ socialIconContainerBg: brandColor });
                                                }}
                                                title={`Container BG ${index + 1}`}
                                            />
                                        ))}
                                        {/* Custom Color Picker */}
                                        <button
                                            type="button"
                                            className="button-brand-color button-brand-color-small custom-color-btn"
                                            onClick={() => document.getElementById('social-container-bg-custom').click()}
                                            title="Custom Container BG"
                                            style={{
                                                background: `linear-gradient(135deg, 
                                                    ${element.socialIconContainerBg || colors.color1 || '#667eea'} 0%, 
                                                    ${element.socialIconContainerBg || colors.color1 || '#667eea'} 100%)`,
                                                border: '2px solid rgba(255, 255, 255, 0.5)'
                                            }}
                                        >
                                            <i className="fas fa-palette" style={{ fontSize: '10px', color: 'white' }}></i>
                                            <input
                                                id="social-container-bg-custom"
                                                type="color"
                                                value={element.socialIconContainerBg || colors.color1 || '#667eea'}
                                                onChange={(e) => onStyleChange({ socialIconContainerBg: e.target.value })}
                                                style={{ display: 'none' }}
                                            />
                                        </button>
                                    </div>
                                </div>
                                
                                {/* Container Padding */}
                                <div className="footer-slider-control">
                                    <span className="footer-slider-label">Padding</span>
                                    <input 
                                        type="range"
                                        className="footer-slider"
                                        min="8"
                                        max="24"
                                        value={element.socialIconContainerPadding || 12}
                                        onChange={(e) => onStyleChange({ socialIconContainerPadding: parseInt(e.target.value) })}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <span className="footer-slider-value">{element.socialIconContainerPadding || 12}px</span>
                                </div>
                                
                                {/* Container Border Toggle */}
                                <div className="footer-toggle-row">
                                    <label>Container Border</label>
                                    <label className="footer-switch">
                                        <input 
                                            type="checkbox"
                                            checked={element.socialIconContainerBorderEnabled || false}
                                            onChange={(e) => onStyleChange({ socialIconContainerBorderEnabled: e.target.checked })}
                                        />
                                        <span className="footer-slider"></span>
                                    </label>
                                </div>
                                
                                {/* Border Settings */}
                                {element.socialIconContainerBorderEnabled && (
                                    <>
                                        <div className="footer-slider-control">
                                            <span className="footer-slider-label">Width</span>
                                            <input 
                                                type="range"
                                                className="footer-slider"
                                                min="1"
                                                max="6"
                                                value={element.socialIconContainerBorderWidth || 2}
                                                onChange={(e) => onStyleChange({ socialIconContainerBorderWidth: parseInt(e.target.value) })}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                            <span className="footer-slider-value">{element.socialIconContainerBorderWidth || 2}px</span>
                                        </div>
                                        
                                        {/* Border Color - Brand Colors */}
                                        <div className="footer-color-group">
                                            <span className="footer-color-label">Border Color</span>
                                            <div className="button-brand-colors button-brand-colors-small">
                                                {Object.values(colors).map((brandColor, index) => (
                                                    <button
                                                        key={index}
                                                        type="button"
                                                        className="button-brand-color button-brand-color-small"
                                                        style={{ backgroundColor: brandColor }}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            onStyleChange({ socialIconContainerBorderColor: brandColor });
                                                        }}
                                                        title={`Border Color ${index + 1}`}
                                                    />
                                                ))}
                                                {/* Custom Color Picker */}
                                                <button
                                                    type="button"
                                                    className="button-brand-color button-brand-color-small custom-color-btn"
                                                    onClick={() => document.getElementById('social-border-custom').click()}
                                                    title="Custom Border Color"
                                                    style={{
                                                        background: `linear-gradient(135deg, 
                                                            ${element.socialIconContainerBorderColor || '#ffffff'} 0%, 
                                                            ${element.socialIconContainerBorderColor || '#ffffff'} 100%)`,
                                                        border: '2px solid rgba(255, 255, 255, 0.5)'
                                                    }}
                                                >
                                                    <i className="fas fa-palette" style={{ fontSize: '10px', color: 'white' }}></i>
                                                    <input
                                                        id="social-border-custom"
                                                        type="color"
                                                        value={element.socialIconContainerBorderColor || '#ffffff'}
                                                        onChange={(e) => onStyleChange({ socialIconContainerBorderColor: e.target.value })}
                                                        style={{ display: 'none' }}
                                                    />
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                                
                                <div className="footer-divider"></div>
                            </>
                        )}
                        
                        <div className="footer-device-toggle">
                            <button 
                                className={device === 'desktop' ? 'active' : ''}
                                onClick={() => setDevice('desktop')}
                            >
                                Desktop
                            </button>
                            <button 
                                className={device === 'mobile' ? 'active' : ''}
                                onClick={() => setDevice('mobile')}
                            >
                                Mobile
                            </button>
                        </div>
                        
                        <div className="footer-slider-control">
                            <span className="footer-slider-label">Icon Size</span>
                            <input 
                                type="range"
                                className="footer-slider"
                                min="16"
                                max="40"
                                value={device === 'mobile' 
                                    ? (element.socialIconSizeMobile || element.socialIconSize || 20)
                                    : (element.socialIconSize || 24)
                                }
                                onChange={(e) => onStyleChange(
                                    device === 'mobile' 
                                        ? { socialIconSizeMobile: parseInt(e.target.value) }
                                        : { socialIconSize: parseInt(e.target.value) }
                                )}
                                onClick={(e) => e.stopPropagation()}
                            />
                            <span className="footer-slider-value">{device === 'mobile' 
                                ? (element.socialIconSizeMobile || element.socialIconSize || 20)
                                : (element.socialIconSize || 24)}px</span>
                        </div>
                        
                        <div className="footer-divider"></div>
                        
                        {(element.socialLinks || []).map((social, index) => (
                            <div key={index} className="footer-social-item">
                                <div className="footer-social-row">
                                    <select
                                        value={social.icon || 'fa-link'}
                                        onChange={(e) => handleUpdateSocialLink(index, 'icon', e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {socialIconOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    <button 
                                        className="footer-btn-delete"
                                        onClick={() => handleDeleteSocialLink(index)}
                                    >
                                        <i className="fas fa-trash-alt"></i>
                                    </button>
                                </div>
                                <input 
                                    type="url"
                                    placeholder="https://..."
                                    value={social.url || ''}
                                    onChange={(e) => handleUpdateSocialLink(index, 'url', e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                        ))}
                        
                        <button className="footer-btn-add" onClick={handleAddSocialLink}>
                            <i className="fas fa-plus"></i>
                            Add Social Link
                        </button>
                    </div>
                );

            case 'links':
                return (
                    <div className="footer-section-content">
                        <div className="footer-device-toggle">
                            <button 
                                className={device === 'desktop' ? 'active' : ''}
                                onClick={() => setDevice('desktop')}
                            >
                                Desktop
                            </button>
                            <button 
                                className={device === 'mobile' ? 'active' : ''}
                                onClick={() => setDevice('mobile')}
                            >
                                Mobile
                            </button>
                        </div>
                        
                        <div className="footer-slider-control">
                            <span className="footer-slider-label">Font Size</span>
                            <input 
                                type="range"
                                className="footer-slider"
                                min="12"
                                max="20"
                                value={device === 'mobile' 
                                    ? (element.linkFontSizeMobile || element.linkFontSize || 14)
                                    : (element.linkFontSize || 15)
                                }
                                onChange={(e) => onStyleChange(
                                    device === 'mobile' 
                                        ? { linkFontSizeMobile: parseInt(e.target.value) }
                                        : { linkFontSize: parseInt(e.target.value) }
                                )}
                                onClick={(e) => e.stopPropagation()}
                            />
                            <span className="footer-slider-value">{device === 'mobile' 
                                ? (element.linkFontSizeMobile || element.linkFontSize || 14)
                                : (element.linkFontSize || 15)}px</span>
                        </div>
                        
                        {/* Font Family Selection */}
                        <div className="footer-section-label">Font Family</div>
                        <div className="footer-font-family-section">
                            {/* Brand Fonts */}
                            <div className="footer-font-group">
                                <span className="footer-font-category">
                                    <i className="fas fa-star" style={{ fontSize: '8px', marginRight: '4px' }}></i>
                                    Brand Fonts
                                </span>
                                <div className="footer-font-grid">
                                    <button
                                        className={`footer-font-option ${(element.linkFontFamily === fontContext.globalBigFont) ? 'is-active' : ''}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onStyleChange({ linkFontFamily: fontContext.globalBigFont });
                                        }}
                                        style={{ fontFamily: fontContext.globalBigFont }}
                                    >
                                        Big Font
                                    </button>
                                    <button
                                        className={`footer-font-option ${(element.linkFontFamily === fontContext.globalSmallFont) ? 'is-active' : ''}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onStyleChange({ linkFontFamily: fontContext.globalSmallFont });
                                        }}
                                        style={{ fontFamily: fontContext.globalSmallFont }}
                                    >
                                        Small Font
                                    </button>
                                </div>
                            </div>
                            
                            {/* Custom Fonts */}
                            {fontContext.customFonts && fontContext.customFonts.length > 0 && (
                                <div className="footer-font-group">
                                    <span className="footer-font-category">
                                        <i className="fas fa-upload" style={{ fontSize: '8px', marginRight: '4px' }}></i>
                                        Custom
                                    </span>
                                    <div className="footer-font-grid">
                                        {fontContext.customFonts.map((customFont, idx) => (
                                            <button
                                                key={idx}
                                                className={`footer-font-option ${(element.linkFontFamily === customFont.name) ? 'is-active' : ''}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    onStyleChange({ linkFontFamily: customFont.name });
                                                }}
                                                style={{ fontFamily: customFont.name }}
                                            >
                                                {customFont.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {/* System Fonts - Seamless List (wie Button Toolbar) */}
                            <div className="tiptap-font-family-list">
                                {/* Title Fonts */}
                                {titleFonts
                                    .filter(font => 
                                        font !== fontContext.globalBigFont && 
                                        font !== fontContext.globalSmallFont
                                    )
                                    .map((font, idx) => (
                                        <button
                                            key={`title-${idx}`}
                                            type="button"
                                            className={`tiptap-font-family-option ${(element.linkFontFamily === font) ? 'is-active' : ''}`}
                                            style={{ fontFamily: font }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                onStyleChange({ linkFontFamily: font });
                                            }}
                                            title={font}
                                        >
                                            {font}
                                        </button>
                                    ))
                                }
                                
                                {/* Text Fonts */}
                                {textFonts
                                    .filter(font => 
                                        font !== fontContext.globalBigFont && 
                                        font !== fontContext.globalSmallFont
                                    )
                                    .map((font, idx) => (
                                        <button
                                            key={`text-${idx}`}
                                            type="button"
                                            className={`tiptap-font-family-option ${(element.linkFontFamily === font) ? 'is-active' : ''}`}
                                            style={{ fontFamily: font }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                onStyleChange({ linkFontFamily: font });
                                            }}
                                            title={font}
                                        >
                                            {font}
                                        </button>
                                    ))
                                }
                            </div>
                        </div>
                        
                        <div className="footer-divider"></div>
                        
                        {(element.navigationLinks || []).map((link, index) => (
                            <div key={index} className="footer-link-item">
                                <div className="footer-link-row">
                                    <input 
                                        type="text"
                                        placeholder="Link Label"
                                        value={link.label || ''}
                                        onChange={(e) => handleUpdateNavLink(index, 'label', e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <button 
                                        className="footer-btn-delete"
                                        onClick={() => handleDeleteNavLink(index)}
                                    >
                                        <i className="fas fa-trash-alt"></i>
                                    </button>
                                </div>
                                <input 
                                    type="url"
                                    placeholder="https://..."
                                    value={link.url || ''}
                                    onChange={(e) => handleUpdateNavLink(index, 'url', e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                        ))}
                        
                        <button className="footer-btn-add" onClick={handleAddNavLink}>
                            <i className="fas fa-plus"></i>
                            Add Link
                        </button>
                    </div>
                );

            case 'layout':
                return (
                    <div className="footer-section-content">
                        <div className="footer-section-label">Desktop Layout</div>
                        <div className="footer-layout-grid">
                            <button
                                className={`footer-layout-option ${element.footerLayout === 'centered' ? 'active' : ''}`}
                                onClick={() => onStyleChange({ footerLayout: 'centered' })}
                            >
                                <div className="footer-layout-preview centered">
                                    <div className="preview-box"></div>
                                    <div className="preview-box"></div>
                                    <div className="preview-box"></div>
                                </div>
                                <span>Centered</span>
                            </button>
                            
                            <button
                                className={`footer-layout-option ${element.footerLayout === '3-column' ? 'active' : ''}`}
                                onClick={() => onStyleChange({ footerLayout: '3-column' })}
                            >
                                <div className="footer-layout-preview three-col">
                                    <div className="preview-box"></div>
                                    <div className="preview-box"></div>
                                    <div className="preview-box"></div>
                                </div>
                                <span>3 Columns</span>
                            </button>
                            
                            <button
                                className={`footer-layout-option ${element.footerLayout === '2-column' ? 'active' : ''}`}
                                onClick={() => onStyleChange({ footerLayout: '2-column' })}
                            >
                                <div className="footer-layout-preview two-col">
                                    <div className="preview-box"></div>
                                    <div className="preview-box"></div>
                                </div>
                                <span>2 Columns</span>
                            </button>
                            
                            <button
                                className={`footer-layout-option ${element.footerLayout === 'stacked' ? 'active' : ''}`}
                                onClick={() => onStyleChange({ footerLayout: 'stacked' })}
                            >
                                <div className="footer-layout-preview stacked">
                                    <div className="preview-box"></div>
                                    <div className="preview-box"></div>
                                    <div className="preview-box"></div>
                                </div>
                                <span>Stacked</span>
                            </button>
                            
                            <button
                                className={`footer-layout-option ${element.footerLayout === 'split' ? 'active' : ''}`}
                                onClick={() => onStyleChange({ footerLayout: 'split' })}
                            >
                                <div className="footer-layout-preview split">
                                    <div className="preview-box" style={{ width: '60%' }}></div>
                                    <div className="preview-box" style={{ width: '40%' }}></div>
                                </div>
                                <span>Split</span>
                            </button>
                            
                            <button
                                className={`footer-layout-option ${element.footerLayout === 'asymmetric' ? 'active' : ''}`}
                                onClick={() => onStyleChange({ footerLayout: 'asymmetric' })}
                            >
                                <div className="footer-layout-preview asymmetric">
                                    <div className="preview-box" style={{ width: '30%' }}></div>
                                    <div className="preview-box" style={{ width: '70%' }}></div>
                                </div>
                                <span>Asymmetric</span>
                            </button>
                        </div>
                        
                        <div className="footer-section-label">Mobile Layout</div>
                        <div className="footer-layout-grid">
                            <button
                                className={`footer-layout-option ${(element.footerLayoutMobile || 'stacked') === 'stacked' ? 'active' : ''}`}
                                onClick={() => onStyleChange({ footerLayoutMobile: 'stacked' })}
                            >
                                <div className="footer-layout-preview stacked">
                                    <div className="preview-box"></div>
                                    <div className="preview-box"></div>
                                    <div className="preview-box"></div>
                                </div>
                                <span>Stacked</span>
                            </button>
                            
                            <button
                                className={`footer-layout-option ${element.footerLayoutMobile === 'centered' ? 'active' : ''}`}
                                onClick={() => onStyleChange({ footerLayoutMobile: 'centered' })}
                            >
                                <div className="footer-layout-preview centered">
                                    <div className="preview-box"></div>
                                    <div className="preview-box"></div>
                                    <div className="preview-box"></div>
                                </div>
                                <span>Centered</span>
                            </button>
                        </div>
                        
                        <div className="footer-divider"></div>
                        
                        <div className="footer-section-label">Spacing</div>
                        <div className="footer-device-toggle">
                            <button 
                                className={device === 'desktop' ? 'active' : ''}
                                onClick={() => setDevice('desktop')}
                            >
                                Desktop
                            </button>
                            <button 
                                className={device === 'mobile' ? 'active' : ''}
                                onClick={() => setDevice('mobile')}
                            >
                                Mobile
                            </button>
                        </div>
                        
                        <div className="footer-slider-control">
                            <span className="footer-slider-label">Pad Top</span>
                            <input 
                                type="range"
                                className="footer-slider"
                                min="0"
                                max="150"
                                value={device === 'mobile' 
                                    ? (element.footerPaddingTopMobile || element.footerPaddingTop || 60)
                                    : (element.footerPaddingTop || 80)
                                }
                                onChange={(e) => onStyleChange(
                                    device === 'mobile' 
                                        ? { footerPaddingTopMobile: parseInt(e.target.value) }
                                        : { footerPaddingTop: parseInt(e.target.value) }
                                )}
                                onClick={(e) => e.stopPropagation()}
                            />
                            <span className="footer-slider-value">{device === 'mobile' 
                                ? (element.footerPaddingTopMobile || element.footerPaddingTop || 60)
                                : (element.footerPaddingTop || 80)}px</span>
                        </div>
                        
                        <div className="footer-slider-control">
                            <span className="footer-slider-label">Pad Bot</span>
                            <input 
                                type="range"
                                className="footer-slider"
                                min="0"
                                max="150"
                                value={device === 'mobile' 
                                    ? (element.footerPaddingBottomMobile || element.footerPaddingBottom || 60)
                                    : (element.footerPaddingBottom || 80)
                                }
                                onChange={(e) => onStyleChange(
                                    device === 'mobile' 
                                        ? { footerPaddingBottomMobile: parseInt(e.target.value) }
                                        : { footerPaddingBottom: parseInt(e.target.value) }
                                )}
                                onClick={(e) => e.stopPropagation()}
                            />
                            <span className="footer-slider-value">{device === 'mobile' 
                                ? (element.footerPaddingBottomMobile || element.footerPaddingBottom || 60)
                                : (element.footerPaddingBottom || 80)}px</span>
                        </div>
                        
                        <div className="footer-slider-control">
                            <span className="footer-slider-label">Gap</span>
                            <input 
                                type="range"
                                className="footer-slider"
                                min="0"
                                max="80"
                                value={device === 'mobile' 
                                    ? (element.footerGapMobile || element.footerGap || 20)
                                    : (element.footerGap || 30)
                                }
                                onChange={(e) => onStyleChange(
                                    device === 'mobile' 
                                        ? { footerGapMobile: parseInt(e.target.value) }
                                        : { footerGap: parseInt(e.target.value) }
                                )}
                                onClick={(e) => e.stopPropagation()}
                            />
                            <span className="footer-slider-value">{device === 'mobile' 
                                ? (element.footerGapMobile || element.footerGap || 20)
                                : (element.footerGap || 30)}px</span>
                        </div>
                    </div>
                );

            case 'style':
                return (
                    <div className="footer-section-content">
                        <div className="footer-section-label">Colors</div>
                        
                        {/* Background Color - Brand Colors */}
                        <div className="footer-color-group">
                            <span className="footer-color-label">Background</span>
                            <div className="button-brand-colors">
                                {Object.values(colors).map((brandColor, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className="button-brand-color"
                                        style={{ backgroundColor: brandColor }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onStyleChange({ footerBackgroundColor: brandColor });
                                        }}
                                        title={`Background Color ${index + 1}`}
                                    />
                                ))}
                                {/* Custom Color Picker Button */}
                                <button
                                    type="button"
                                    className="button-brand-color custom-color-btn"
                                    onClick={() => document.getElementById('footer-bg-custom-color').click()}
                                    title="Custom Background Color"
                                    style={{
                                        background: `linear-gradient(135deg, 
                                            ${element.footerBackgroundColor || colors.color1 || '#1a1a1a'} 0%, 
                                            ${element.footerBackgroundColor || colors.color1 || '#1a1a1a'} 100%)`,
                                        border: '2px solid rgba(255, 255, 255, 0.5)'
                                    }}
                                >
                                    <i className="fas fa-palette" style={{ fontSize: '12px', color: 'white' }}></i>
                                    <input
                                        id="footer-bg-custom-color"
                                        type="color"
                                        value={element.footerBackgroundColor || colors.color1 || '#1a1a1a'}
                                        onChange={(e) => onStyleChange({ footerBackgroundColor: e.target.value })}
                                        style={{ display: 'none' }}
                                    />
                                </button>
                            </div>
                        </div>
                        
                        {/* Gradient Toggle */}
                        <div className="footer-custom-toggle-wrapper">
                            <input 
                                type="checkbox"
                                id="footer-gradient-toggle"
                                className="footer-custom-toggle-input"
                                checked={element.footerGradientEnabled || false}
                                onChange={(e) => onStyleChange({ footerGradientEnabled: e.target.checked })}
                            />
                            <label htmlFor="footer-gradient-toggle" className="footer-custom-toggle-slider"></label>
                            <label htmlFor="footer-gradient-toggle" className="footer-custom-toggle-label">
                                Gradient Background
                            </label>
                        </div>
                        
                        {/* Gradient Colors */}
                        {element.footerGradientEnabled && (
                            <div className="footer-gradient-controls">
                                <div className="footer-gradient-row">
                                    <span className="footer-gradient-label">Color 1</span>
                                    <div className="button-brand-colors button-brand-colors-small">
                                        {Object.values(colors).map((brandColor, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                className="button-brand-color button-brand-color-small"
                                                style={{ backgroundColor: brandColor }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    onStyleChange({ footerGradientColor1: brandColor });
                                                }}
                                                title={`Gradient Color 1 - ${index + 1}`}
                                            />
                                        ))}
                                        {/* Custom Color Picker */}
                                        <button
                                            type="button"
                                            className="button-brand-color button-brand-color-small custom-color-btn"
                                            onClick={() => document.getElementById('footer-gradient-color1').click()}
                                            title="Custom Gradient Color 1"
                                            style={{
                                                background: element.footerGradientColor1 || '#667eea',
                                                border: '2px solid rgba(255, 255, 255, 0.5)'
                                            }}
                                        >
                                            <i className="fas fa-palette" style={{ fontSize: '10px', color: 'white' }}></i>
                                            <input
                                                id="footer-gradient-color1"
                                                type="color"
                                                value={element.footerGradientColor1 || '#667eea'}
                                                onChange={(e) => onStyleChange({ footerGradientColor1: e.target.value })}
                                                style={{ display: 'none' }}
                                            />
                                        </button>
                                    </div>
                                </div>
                                
                                <div className="footer-gradient-row">
                                    <span className="footer-gradient-label">Color 2</span>
                                    <div className="button-brand-colors button-brand-colors-small">
                                        {Object.values(colors).map((brandColor, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                className="button-brand-color button-brand-color-small"
                                                style={{ backgroundColor: brandColor }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    onStyleChange({ footerGradientColor2: brandColor });
                                                }}
                                                title={`Gradient Color 2 - ${index + 1}`}
                                            />
                                        ))}
                                        {/* Custom Color Picker */}
                                        <button
                                            type="button"
                                            className="button-brand-color button-brand-color-small custom-color-btn"
                                            onClick={() => document.getElementById('footer-gradient-color2').click()}
                                            title="Custom Gradient Color 2"
                                            style={{
                                                background: element.footerGradientColor2 || '#764ba2',
                                                border: '2px solid rgba(255, 255, 255, 0.5)'
                                            }}
                                        >
                                            <i className="fas fa-palette" style={{ fontSize: '10px', color: 'white' }}></i>
                                            <input
                                                id="footer-gradient-color2"
                                                type="color"
                                                value={element.footerGradientColor2 || '#764ba2'}
                                                onChange={(e) => onStyleChange({ footerGradientColor2: e.target.value })}
                                                style={{ display: 'none' }}
                                            />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* Text Color - Brand Colors */}
                        <div className="footer-color-group">
                            <span className="footer-color-label">Text Color</span>
                            <div className="button-brand-colors">
                                {Object.values(colors).map((brandColor, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className="button-brand-color"
                                        style={{ backgroundColor: brandColor }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onStyleChange({ footerTextColor: brandColor });
                                        }}
                                        title={`Text Color ${index + 1}`}
                                    />
                                ))}
                                {/* Custom Color Picker Button */}
                                <button
                                    type="button"
                                    className="button-brand-color custom-color-btn"
                                    onClick={() => document.getElementById('footer-text-custom-color').click()}
                                    title="Custom Text Color"
                                    style={{
                                        background: `linear-gradient(135deg, 
                                            ${element.footerTextColor || '#ffffff'} 0%, 
                                            ${element.footerTextColor || '#ffffff'} 100%)`,
                                        border: '2px solid rgba(255, 255, 255, 0.5)'
                                    }}
                                >
                                    <i className="fas fa-palette" style={{ fontSize: '12px', color: element.footerTextColor === '#ffffff' ? '#333' : 'white' }}></i>
                                    <input
                                        id="footer-text-custom-color"
                                        type="color"
                                        value={element.footerTextColor || '#ffffff'}
                                        onChange={(e) => onStyleChange({ footerTextColor: e.target.value })}
                                        style={{ display: 'none' }}
                                    />
                                </button>
                            </div>
                        </div>
                        
                        {/* Link Color - Brand Colors */}
                        <div className="footer-color-group">
                            <span className="footer-color-label">Link Color</span>
                            <div className="button-brand-colors">
                                {Object.values(colors).map((brandColor, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className="button-brand-color"
                                        style={{ backgroundColor: brandColor }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onStyleChange({ footerLinkColor: brandColor });
                                        }}
                                        title={`Link Color ${index + 1}`}
                                    />
                                ))}
                                {/* Custom Color Picker Button */}
                                <button
                                    type="button"
                                    className="button-brand-color custom-color-btn"
                                    onClick={() => document.getElementById('footer-link-custom-color').click()}
                                    title="Custom Link Color"
                                    style={{
                                        background: `linear-gradient(135deg, 
                                            ${element.footerLinkColor || '#a0a0a0'} 0%, 
                                            ${element.footerLinkColor || '#a0a0a0'} 100%)`,
                                        border: '2px solid rgba(255, 255, 255, 0.5)'
                                    }}
                                >
                                    <i className="fas fa-palette" style={{ fontSize: '12px', color: 'white' }}></i>
                                    <input
                                        id="footer-link-custom-color"
                                        type="color"
                                        value={element.footerLinkColor || '#a0a0a0'}
                                        onChange={(e) => onStyleChange({ footerLinkColor: e.target.value })}
                                        style={{ display: 'none' }}
                                    />
                                </button>
                            </div>
                        </div>
                        
                        <div className="footer-divider"></div>
                        
                        <div className="footer-section-label">Border</div>
                        
                        <div className="footer-toggle-row">
                            <label>Top Border</label>
                            <label className="footer-switch">
                                <input 
                                    type="checkbox"
                                    checked={element.footerBorderTopEnabled || false}
                                    onChange={(e) => onStyleChange({ footerBorderTopEnabled: e.target.checked })}
                                />
                                <span className="footer-slider"></span>
                            </label>
                        </div>
                        
                        {element.footerBorderTopEnabled && (
                            <>
                                <div className="footer-slider-control">
                                    <span className="footer-slider-label">Width</span>
                                    <input 
                                        type="range"
                                        className="footer-slider"
                                        min="1"
                                        max="10"
                                        value={element.borderTopWidth || 1}
                                        onChange={(e) => onStyleChange({ borderTopWidth: parseInt(e.target.value) })}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <span className="footer-slider-value">{element.borderTopWidth || 1}px</span>
                                </div>
                                
                                {/* Border Color - Brand Colors */}
                                <div className="footer-color-group">
                                    <span className="footer-color-label">Border Color</span>
                                    <div className="button-brand-colors button-brand-colors-small">
                                        {Object.values(colors).map((brandColor, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                className="button-brand-color button-brand-color-small"
                                                style={{ backgroundColor: brandColor }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    onStyleChange({ footerBorderTopColor: brandColor });
                                                }}
                                                title={`Border Color ${index + 1}`}
                                            />
                                        ))}
                                        {/* Custom Color Picker Button */}
                                        <button
                                            type="button"
                                            className="button-brand-color button-brand-color-small custom-color-btn"
                                            onClick={() => document.getElementById('footer-border-custom-color').click()}
                                            title="Custom Border Color"
                                            style={{
                                                background: `linear-gradient(135deg, 
                                                    ${element.footerBorderTopColor || '#333333'} 0%, 
                                                    ${element.footerBorderTopColor || '#333333'} 100%)`,
                                                border: '2px solid rgba(255, 255, 255, 0.5)'
                                            }}
                                        >
                                            <i className="fas fa-palette" style={{ fontSize: '10px', color: 'white' }}></i>
                                            <input
                                                id="footer-border-custom-color"
                                                type="color"
                                                value={element.footerBorderTopColor || '#333333'}
                                                onChange={(e) => onStyleChange({ footerBorderTopColor: e.target.value })}
                                                style={{ display: 'none' }}
                                            />
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                        
                        <div className="footer-divider"></div>
                        
                        {/* Premium Styling Options */}
                        <div className="footer-section-label">Premium Styling</div>
                        
                        {/* Container Max Width */}
                        <div className="footer-slider-control">
                            <span className="footer-slider-label">Max Width</span>
                            <input 
                                type="range"
                                className="footer-slider"
                                min="800"
                                max="2000"
                                step="50"
                                value={element.footerMaxWidth || 1400}
                                onChange={(e) => onStyleChange({ footerMaxWidth: parseInt(e.target.value) })}
                                onClick={(e) => e.stopPropagation()}
                            />
                            <span className="footer-slider-value">{element.footerMaxWidth || 1400}px</span>
                        </div>
                        
                        {/* Border Radius */}
                        <div className="footer-slider-control">
                            <span className="footer-slider-label">Radius</span>
                            <input 
                                type="range"
                                className="footer-slider"
                                min="0"
                                max="50"
                                value={element.footerBorderRadius || 0}
                                onChange={(e) => onStyleChange({ footerBorderRadius: parseInt(e.target.value) })}
                                onClick={(e) => e.stopPropagation()}
                            />
                            <span className="footer-slider-value">{element.footerBorderRadius || 0}px</span>
                        </div>
                        
                        {/* Box Shadow Toggle */}
                        <div className="footer-custom-toggle-wrapper">
                            <input 
                                type="checkbox"
                                id="footer-shadow-toggle"
                                className="footer-custom-toggle-input"
                                checked={element.footerShadowEnabled || false}
                                onChange={(e) => onStyleChange({ footerShadowEnabled: e.target.checked })}
                            />
                            <label htmlFor="footer-shadow-toggle" className="footer-custom-toggle-slider"></label>
                            <label htmlFor="footer-shadow-toggle" className="footer-custom-toggle-label">
                                Box Shadow
                            </label>
                        </div>
                        
                        {/* Shadow Controls */}
                        {element.footerShadowEnabled && (
                            <>
                                <div className="footer-slider-control">
                                    <span className="footer-slider-label">Intensity</span>
                                    <input 
                                        type="range"
                                        className="footer-slider"
                                        min="0"
                                        max="50"
                                        value={element.footerShadowIntensity || 15}
                                        onChange={(e) => onStyleChange({ footerShadowIntensity: parseInt(e.target.value) })}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <span className="footer-slider-value">{element.footerShadowIntensity || 15}px</span>
                                </div>
                                
                                <div className="footer-slider-control">
                                    <span className="footer-slider-label">Opacity</span>
                                    <input 
                                        type="range"
                                        className="footer-slider"
                                        min="0"
                                        max="100"
                                        value={element.footerShadowOpacity || 20}
                                        onChange={(e) => onStyleChange({ footerShadowOpacity: parseInt(e.target.value) })}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <span className="footer-slider-value">{element.footerShadowOpacity || 20}%</span>
                                </div>
                            </>
                        )}
                        
                        {/* Background Opacity (only wenn kein Gradient) */}
                        {!element.footerGradientEnabled && (
                            <div className="footer-slider-control">
                                <span className="footer-slider-label">BG Opacity</span>
                                <input 
                                    type="range"
                                    className="footer-slider"
                                    min="0"
                                    max="100"
                                    value={element.footerBackgroundOpacity ?? 100}
                                    onChange={(e) => onStyleChange({ footerBackgroundOpacity: parseInt(e.target.value) })}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <span className="footer-slider-value">{element.footerBackgroundOpacity ?? 100}%</span>
                            </div>
                        )}
                        
                        {/* Divider Line Toggle */}
                        <div className="footer-custom-toggle-wrapper">
                            <input 
                                type="checkbox"
                                id="footer-divider-toggle"
                                className="footer-custom-toggle-input"
                                checked={element.footerDividerEnabled || false}
                                onChange={(e) => onStyleChange({ footerDividerEnabled: e.target.checked })}
                            />
                            <label htmlFor="footer-divider-toggle" className="footer-custom-toggle-slider"></label>
                            <label htmlFor="footer-divider-toggle" className="footer-custom-toggle-label">
                                Section Dividers
                            </label>
                        </div>
                        
                        {/* Link Hover Animation */}
                        <div className="footer-custom-toggle-wrapper">
                            <input 
                                type="checkbox"
                                id="footer-link-hover-toggle"
                                className="footer-custom-toggle-input"
                                checked={element.footerLinkHoverAnimation ?? true}
                                onChange={(e) => onStyleChange({ footerLinkHoverAnimation: e.target.checked })}
                            />
                            <label htmlFor="footer-link-hover-toggle" className="footer-custom-toggle-slider"></label>
                            <label htmlFor="footer-link-hover-toggle" className="footer-custom-toggle-label">
                                Link Hover Effects
                            </label>
                        </div>
                    </div>
                );

            case 'fonts':
                return (
                    <div className="footer-section-content">
                        <div className="footer-section-label">Font Family</div>
                        
                        {/* Font Family Selection */}
                        <div className="tiptap-font-family-list">
                            {/* ⭐ BRAND FONTS ZUERST - Mit Badge */}
                            {fonts?.globalBigFont && (
                                <button
                                    key="brand-big-font"
                                    type="button"
                                    className={`tiptap-font-family-option brand-font ${(element.sloganFontFamily || element.linkFontFamily) === fonts.globalBigFont ? 'is-active' : ''}`}
                                    style={{ fontFamily: fonts.globalBigFont }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('🎨 Footer Font Family clicked (Brand Big):', fonts.globalBigFont);
                                        onStyleChange({ 
                                            sloganFontFamily: fonts.globalBigFont,
                                            linkFontFamily: fonts.globalBigFont 
                                        });
                                    }}
                                    title={`${fonts.globalBigFont} (Brand Headline Font)`}
                                >
                                    ⭐ {fonts.globalBigFont}
                                </button>
                            )}
                            
                            {fonts?.globalSmallFont && fonts.globalSmallFont !== fonts.globalBigFont && (
                                <button
                                    key="brand-small-font"
                                    type="button"
                                    className={`tiptap-font-family-option brand-font ${(element.sloganFontFamily || element.linkFontFamily) === fonts.globalSmallFont ? 'is-active' : ''}`}
                                    style={{ fontFamily: fonts.globalSmallFont }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('🎨 Footer Font Family clicked (Brand Small):', fonts.globalSmallFont);
                                        onStyleChange({ 
                                            sloganFontFamily: fonts.globalSmallFont,
                                            linkFontFamily: fonts.globalSmallFont 
                                        });
                                    }}
                                    title={`${fonts.globalSmallFont} (Brand Body Font)`}
                                >
                                    ⭐ {fonts.globalSmallFont}
                                </button>
                            )}
                            
                            {/* Divider nach Brand Fonts */}
                            {(fonts?.globalBigFont || fonts?.globalSmallFont) && (
                                <div style={{
                                    height: '1px',
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    margin: '8px 0'
                                }}></div>
                            )}
                            
                            {/* Custom Fonts - gefiltert (ohne Brand Fonts) */}
                            {fontContext.customFonts && fontContext.customFonts.length > 0 && (
                                <>
                                    {fontContext.customFonts
                                        .filter(customFont => 
                                            customFont.name !== fonts?.globalBigFont && 
                                            customFont.name !== fonts?.globalSmallFont
                                        )
                                        .map((customFont, index) => (
                                        <button
                                            key={`custom-${index}`}
                                            type="button"
                                            className={`tiptap-font-family-option ${(element.sloganFontFamily || element.linkFontFamily) === customFont.name ? 'is-active' : ''}`}
                                            style={{ fontFamily: customFont.name }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                console.log('🎨 Footer Font Family clicked:', customFont.name);
                                                onStyleChange({ 
                                                    sloganFontFamily: customFont.name,
                                                    linkFontFamily: customFont.name 
                                                });
                                            }}
                                            title={customFont.name}
                                        >
                                            {customFont.name}
                                        </button>
                                    ))}
                                </>
                            )}

                            {/* Title Fonts - gefiltert (ohne Brand Fonts) */}
                            {titleFonts
                                .filter(font => 
                                    font !== fonts?.globalBigFont && 
                                    font !== fonts?.globalSmallFont
                                )
                                .map((font, index) => {
                                const isActive = (element.sloganFontFamily || element.linkFontFamily) === font;
                                return (
                                <button
                                    key={`title-${index}`}
                                    type="button"
                                    className={`tiptap-font-family-option ${isActive ? 'is-active' : ''}`}
                                    style={{ fontFamily: font }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('🎨 Footer Font Family clicked:', font);
                                        onStyleChange({ 
                                            sloganFontFamily: font,
                                            linkFontFamily: font 
                                        });
                                    }}
                                    title={font}
                                >
                                    {font}
                                </button>
                                );
                            })}

                            {/* Text Fonts - gefiltert (ohne Brand Fonts) */}
                            {textFonts
                                .filter(font => 
                                    font !== fonts?.globalBigFont && 
                                    font !== fonts?.globalSmallFont
                                )
                                .map((font, index) => (
                                <button
                                    key={`text-${index}`}
                                    type="button"
                                    className={`tiptap-font-family-option ${(element.sloganFontFamily || element.linkFontFamily) === font ? 'is-active' : ''}`}
                                    style={{ fontFamily: font }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('🎨 Footer Font Family clicked:', font);
                                        onStyleChange({ 
                                            sloganFontFamily: font,
                                            linkFontFamily: font 
                                        });
                                    }}
                                    title={font}
                                >
                                    {font}
                                </button>
                            ))}
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    // 🚫 Don't render if not shown yet
    if (!showToolbar) {
        return null;
    }
    
    const renderToolbar = () => (
        <div 
            className={`footer-floating-toolbar-portal ${expandedSection ? 'expanded' : ''} ${isDragging ? 'dragging' : ''}`}
            ref={toolbarRef}
            style={{
                position: 'fixed',
                top: `${toolbarPosition.top}px`,
                left: `${toolbarPosition.left}px`,
                transform: 'translateX(-50%)',
                zIndex: 999999
            }}
            onMouseDown={(e) => {
                // ✅ CRITICAL FIX: Stoppe ALLE mousedown Events auf Portal-Level
                e.stopPropagation();
            }}
            onClick={(e) => {
                // ✅ CRITICAL FIX: Stoppe ALLE click Events auf Portal-Level
                e.stopPropagation();
            }}
        >
            <div className="footer-toolbar">
                {/* Compact Row - Icon Buttons */}
                <div className="footer-toolbar-row">
                    {/* 🎯 Drag Handle Icon - Links */}
                    <div 
                        className="footer-drag-handle"
                        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                        title="Drag to move toolbar"
                        onMouseDown={handleMouseDown}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        <i className="fas fa-grip-vertical"></i>
                    </div>
                    
                    <button
                        className={`footer-toolbar-btn ${expandedSection === 'logo' ? 'is-active' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'logo' ? null : 'logo');
                        }}
                        title="Logo"
                    >
                        <i className="fas fa-image"></i>
                    </button>
                    
                    <button
                        className={`footer-toolbar-btn ${expandedSection === 'slogan' ? 'is-active' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'slogan' ? null : 'slogan');
                        }}
                        title="Slogan"
                    >
                        <i className="fas fa-comment-alt"></i>
                    </button>
                    
                    <button
                        className={`footer-toolbar-btn ${expandedSection === 'socials' ? 'is-active' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'socials' ? null : 'socials');
                        }}
                        title="Social Media"
                    >
                        <i className="fas fa-share-alt"></i>
                    </button>
                    
                    <button
                        className={`footer-toolbar-btn ${expandedSection === 'links' ? 'is-active' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'links' ? null : 'links');
                        }}
                        title="Navigation Links"
                    >
                        <i className="fas fa-link"></i>
                    </button>
                    
                    <button
                        className={`footer-toolbar-btn ${expandedSection === 'layout' ? 'is-active' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'layout' ? null : 'layout');
                        }}
                        title="Layout"
                    >
                        <i className="fas fa-th-large"></i>
                    </button>
                    
                    <button
                        className={`footer-toolbar-btn ${expandedSection === 'style' ? 'is-active' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'style' ? null : 'style');
                        }}
                        title="Style"
                    >
                        <i className="fas fa-palette"></i>
                    </button>
                    
                    <button
                        className={`footer-toolbar-btn ${expandedSection === 'fonts' ? 'is-active' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'fonts' ? null : 'fonts');
                        }}
                        title="Font Family"
                    >
                        <i className="fas fa-font"></i>
                    </button>
                    
                    {/* 🎯 Vertikale Trennlinie */}
                    <div className="footer-toolbar-separator"></div>
                    
                    {/* Close Button */}
                    <button
                        className="footer-toolbar-btn footer-close-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            onClose();
                        }}
                        title="Close Toolbar"
                    >
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                {/* Expanded Section */}
                {expandedSection && (
                    <div className="footer-expanded-section">
                        {renderExpandedContent()}
                    </div>
                )}
            </div>
        </div>
    );

    return ReactDOM.createPortal(renderToolbar(), document.body);
}

export default FooterFloatingToolbar;
